<template>
  <div class="analytics-page cell auto cell-block-y">
    <div class="analytics-wrap">
      <div class="analytics-header">
        <div>
          <div class="analytics-title-row">
            <h1>iWALY Business Analytics</h1>
            <span class="analytics-badge">BI · V1</span>
          </div>
          <p>
            Продажи, клиенты, retention, география и операционная эффективность — на данных текущего backend.
          </p>
        </div>
        <div class="analytics-actions">
          <span v-if="generatedAt" class="updated-at">Обновлено {{ formatDateTime(generatedAt) }}</span>
          <button class="analytics-btn secondary" :disabled="loading" @click="downloadCsv">CSV</button>
          <button class="analytics-btn primary" :disabled="loading" @click="fetchDashboard">
            {{ loading ? 'Обновляю…' : 'Обновить' }}
          </button>
        </div>
      </div>

      <div class="quick-range-row">
        <button
          v-for="item in quickRanges"
          :key="item.value"
          :class="['quick-range', { active: activeQuickRange === item.value }]"
          @click="setQuickRange(item.value)"
        >
          {{ item.label }}
        </button>
      </div>

      <div class="filter-panel">
        <label>
          <span>С</span>
          <input v-model="filters.from" type="date">
        </label>
        <label>
          <span>По</span>
          <input v-model="filters.to" type="date">
        </label>
        <label>
          <span>Сравнить</span>
          <select v-model="filters.compare">
            <option value="previous">Предыдущий период</option>
            <option value="year">Прошлый год</option>
            <option value="none">Без сравнения</option>
          </select>
        </label>
        <label>
          <span>Группировка</span>
          <select v-model="filters.granularity">
            <option value="day">День</option>
            <option value="week">Неделя</option>
            <option value="month">Месяц</option>
          </select>
        </label>
        <label>
          <span>Регион</span>
          <select v-model="filters.regionId">
            <option value="">Все регионы</option>
            <option v-for="item in optionList('regions')" :key="item.id" :value="String(item.id)">{{ item.name }}</option>
          </select>
        </label>
        <label>
          <span>Категория</span>
          <select v-model="filters.categoryId">
            <option value="">Все категории</option>
            <option v-for="item in optionList('categories')" :key="item.id" :value="String(item.id)">{{ item.name }}</option>
          </select>
        </label>
        <label>
          <span>Партнёр</span>
          <select v-model="filters.partnerId">
            <option value="">Все партнёры</option>
            <option v-for="item in optionList('partners')" :key="item.id" :value="String(item.id)">{{ item.name }}</option>
          </select>
        </label>
        <label>
          <span>Канал оформления</span>
          <select v-model="filters.source">
            <option value="all">Все</option>
            <option v-for="item in optionList('sources')" :key="item.id" :value="item.id">{{ item.name }}</option>
          </select>
        </label>
        <label>
          <span>Статус</span>
          <select v-model="filters.state">
            <option value="all">Все статусы</option>
            <option v-for="item in optionList('states')" :key="item.id" :value="item.id">{{ item.name }}</option>
          </select>
        </label>
        <div class="filter-apply">
          <button class="analytics-btn primary" :disabled="loading" @click="applyFilters">Применить</button>
          <button class="analytics-btn secondary" :disabled="loading" @click="resetDimensionFilters">Сбросить</button>
        </div>
      </div>

      <div v-if="error" class="analytics-error">
        <strong>Не удалось получить аналитику.</strong> {{ error }}
      </div>

      <div class="subtabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['subtab', { active: activeTab === tab.id }]"
          @click="switchTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>

      <template v-if="dashboard">
        <section v-if="activeTab === 'overview'" class="analytics-section">
          <div class="kpi-grid">
            <button v-for="card in primaryKpis" :key="card.key" class="kpi-card" @click="selectChartMetric(card.key)">
              <div class="kpi-topline">
                <span>{{ card.label }}</span>
                <span v-if="card.hint" class="kpi-hint" :title="card.hint">i</span>
              </div>
              <div class="kpi-value">{{ formatMetric(card.metric) }}</div>
              <div class="kpi-comparison">
                <span :class="deltaClass(card.metric && card.metric.deltaPercent)">{{ formatDelta(card.metric && card.metric.deltaPercent) }}</span>
                <span v-if="hasComparison">к сравнению</span>
              </div>
            </button>
          </div>

          <div class="panel trend-panel">
            <div class="panel-heading analytics-panel-heading">
              <div>
                <h2>Динамика бизнеса</h2>
                <p>{{ periodCaption }}</p>
              </div>
              <div class="metric-switcher">
                <button
                  v-for="metric in chartMetrics"
                  :key="metric.key"
                  :class="{ active: chartMetric === metric.key }"
                  @click="chartMetric = metric.key"
                >
                  {{ metric.label }}
                </button>
              </div>
            </div>
            <div class="trend-chart-wrap">
              <svg class="trend-chart" viewBox="0 0 920 300" preserveAspectRatio="none">
                <line v-for="y in [40, 100, 160, 220, 280]" :key="y" x1="48" :y1="y" x2="900" :y2="y" class="grid-line" />
                <polyline v-if="comparisonChartPoints" :points="comparisonChartPoints" class="trend-line comparison" />
                <polyline v-if="currentChartPoints" :points="currentChartPoints" class="trend-line current" />
              </svg>
              <div class="chart-legend">
                <span><i class="legend-line current"></i>Текущий период</span>
                <span v-if="hasComparison"><i class="legend-line comparison"></i>{{ comparisonLabel }}</span>
              </div>
              <div v-if="!hasSeriesData" class="chart-empty">В выбранном периоде нет данных для графика.</div>
            </div>
            <div class="axis-labels">
              <span v-for="label in chartAxisLabels" :key="label.key">{{ label.label }}</span>
            </div>
          </div>

          <div class="two-column-grid">
            <div class="panel">
              <div class="panel-heading analytics-panel-heading">
                <div><h2>Клиентская воронка</h2><p>Когорта регистраций выбранного периода</p></div>
              </div>
              <div class="funnel-list">
                <div v-for="step in dashboard.funnel" :key="step.key" class="funnel-row">
                  <div class="funnel-label"><strong>{{ step.label }}</strong><span>{{ integer(step.value) }}</span></div>
                  <div class="progress-track"><div class="progress-fill" :style="{ width: `${Math.max(2, step.conversionFromRegistration)}%` }"></div></div>
                  <div class="funnel-meta">{{ percent(step.conversionFromRegistration) }} от регистраций · {{ percent(step.conversionFromPrevious) }} с прошлого шага</div>
                </div>
              </div>
            </div>

            <div class="panel">
              <div class="panel-heading analytics-panel-heading">
                <div><h2>Канал оформления</h2><p>Это не UTM attribution: источник определяется по данным заказа</p></div>
              </div>
              <div class="source-list">
                <div v-for="source in dashboard.sources" :key="source.key" class="source-row">
                  <div class="source-head"><strong>{{ source.label }}</strong><span>{{ money(source.gmv) }}</span></div>
                  <div class="progress-track"><div class="progress-fill muted" :style="{ width: `${source.gmvShare}%` }"></div></div>
                  <div class="source-meta">{{ integer(source.orders) }} заказов · {{ integer(source.customers) }} клиентов · {{ percent(source.gmvShare) }} GMV</div>
                </div>
              </div>
            </div>
          </div>

          <div class="two-column-grid">
            <div class="panel">
              <div class="panel-heading analytics-panel-heading"><div><h2>ТОП регионов</h2><p>По GMV оплаченных заказов</p></div></div>
              <div class="ranking-list">
                <div v-for="(region, index) in dashboard.regions.slice(0, 8)" :key="region.id" class="ranking-row">
                  <span class="rank">{{ index + 1 }}</span>
                  <div class="ranking-main"><strong>{{ region.name }}</strong><span>{{ integer(region.orders) }} заказов · AOV {{ money(region.aov) }}</span></div>
                  <div class="ranking-value">{{ money(region.gmv) }}</div>
                </div>
              </div>
            </div>
            <div class="panel">
              <div class="panel-heading analytics-panel-heading"><div><h2>Операционный пульс</h2><p>Исполнение и качество</p></div></div>
              <div class="mini-kpi-grid">
                <div class="mini-kpi"><span>Fulfillment</span><strong>{{ percent(dashboard.operations.fulfillmentRate) }}</strong></div>
                <div class="mini-kpi"><span>Отмены</span><strong>{{ percent(dashboard.operations.cancellationRate) }}</strong></div>
                <div class="mini-kpi"><span>Споры</span><strong>{{ percent(dashboard.operations.disputeRate) }}</strong></div>
                <div class="mini-kpi"><span>Медиана выполнения</span><strong>{{ number(dashboard.operations.medianFulfillmentDays) }} дн.</strong></div>
                <div class="mini-kpi"><span>Активные исполнители</span><strong>{{ integer(dashboard.operations.activeContractors) }}</strong></div>
                <div class="mini-kpi"><span>Средний рейтинг</span><strong>{{ number(dashboard.operations.averageRating) }} / 5</strong></div>
              </div>
            </div>
          </div>
        </section>

        <section v-if="activeTab === 'sales'" class="analytics-section">
          <div class="kpi-grid compact">
            <KpiSimple label="GMV" :value="money(kpiValue('gmv'))" />
            <KpiSimple label="Сервисная комиссия" :value="money(kpiValue('serviceCommission'))" />
            <KpiSimple label="Take rate" :value="percent(kpiValue('takeRate'))" />
            <KpiSimple label="Средний чек" :value="money(kpiValue('aov'))" />
            <KpiSimple label="Выплаты исполнителям" :value="money(dashboard.operations.contractorPayout)" />
            <KpiSimple label="Партнёрские выплаты" :value="money(dashboard.operations.partnerPayout)" />
          </div>
          <div class="panel">
            <div class="panel-heading analytics-panel-heading">
              <div><h2>Категории услуг</h2><p>Catalog volume — стоимость позиций по прайсу; не подменяет фактический GMV заказа</p></div>
            </div>
            <div class="responsive-table">
              <table>
                <thead><tr><th>Категория</th><th>Заказы</th><th>Catalog volume</th><th>Средний чек заказа</th></tr></thead>
                <tbody>
                  <tr v-for="item in dashboard.services" :key="item.id">
                    <td><strong>{{ item.name }}</strong></td><td>{{ integer(item.orders) }}</td><td>{{ money(item.catalogVolume) }}</td><td>{{ money(item.avgOrderPrice) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section v-if="activeTab === 'customers'" class="analytics-section">
          <div class="kpi-grid compact">
            <KpiSimple label="Регистрации" :value="integer(dashboard.customers.registrations)" />
            <KpiSimple label="Платящие клиенты" :value="integer(dashboard.customers.payingCustomers)" />
            <KpiSimple label="Новые платящие" :value="integer(dashboard.customers.newPayingCustomers)" />
            <KpiSimple label="Повторные клиенты" :value="integer(dashboard.customers.repeatCustomers)" />
            <KpiSimple label="Repeat rate" :value="percent(dashboard.customers.repeatRate)" />
            <KpiSimple label="GMV повторных" :value="percent(dashboard.customers.repeatGmvShare)" />
            <KpiSimple label="Новые объекты" :value="integer(dashboard.customers.subjectsCreated)" />
            <KpiSimple label="До второго заказа" :value="`${number(dashboard.customers.medianDaysToSecondOrder)} дн.`" />
          </div>
          <div class="panel">
            <div class="panel-heading analytics-panel-heading"><div><h2>Воронка клиента</h2><p>Не page funnel: считаем реальные сущности БД</p></div></div>
            <div class="funnel-horizontal">
              <div v-for="step in dashboard.funnel" :key="step.key" class="funnel-stage">
                <span>{{ step.label }}</span><strong>{{ integer(step.value) }}</strong><small>{{ percent(step.conversionFromRegistration) }}</small>
              </div>
            </div>
          </div>
        </section>

        <section v-if="activeTab === 'audience'" class="analytics-section">
          <div class="definition-note">
            <strong>Область применения фильтров.</strong>
            Здесь действуют только даты и сравнение периодов. Фильтры региона, категории, партнёра,
            канала оформления, статуса заказа и группировки относятся к коммерческой аналитике и на эту вкладку не влияют.
            Метрики устройств и последней активности — текущие снимки, независимо от периода.
          </div>
          <div v-if="!dashboard.audience" class="analytics-error">Backend не вернул блок аудитории. Убедитесь, что новая версия backend развернута, и обновите страницу.</div>
          <div v-else-if="!dashboard.audience.available" class="analytics-error">
            Данные аудитории пока недоступны: {{ dashboard.audience.reason }}
          </div>
          <template v-else>
            <div class="kpi-grid compact">
              <KpiSimple label="Регистрации заказчиков" :value="integer(dashboard.audience.summary.registrations)" />
              <KpiSimple label="Источник определён" :value="percent(dashboard.audience.summary.knownSourcePercent)" />
              <KpiSimple label="Активность за 30 суток*" :value="integer(dashboard.audience.lastActivity.last30d)" />
              <KpiSimple label="Пользователи с Push-токеном*" :value="integer(dashboard.audience.pushDevices.registeredUsers)" />
              <KpiSimple label="Зарегистрированные токены*" :value="integer(dashboard.audience.pushDevices.registeredTokens)" />
              <KpiSimple label="Последний вход в периоде" :value="integer(loginSampleUsers)" />
            </div>
            <div v-if="dashboard.audience.summary.previousRegistrations !== null" class="definition-note">
              Регистраций заказчиков за период сравнения:
              <strong>{{ integer(dashboard.audience.summary.previousRegistrations) }}</strong>.
            </div>

            <div class="panel">
              <div class="panel-heading analytics-panel-heading">
                <div><h2>Первый источник регистрации → покупка</h2>
                  <p>Когорта новых заказчиков. Учитываются оплаченные заказы, созданные до конца выбранного периода, по текущему статусу оплаты.</p></div>
              </div>
              <div class="responsive-table">
                <table>
                  <thead><tr><th>Источник</th><th>Регистрации</th><th>Доля</th><th>Платящие</th><th>Конверсия</th><th>GMV</th></tr></thead>
                  <tbody>
                    <tr v-for="item in dashboard.audience.registrationSources" :key="item.source">
                      <td><strong>{{ audienceLabel(item.source) }}</strong></td>
                      <td>{{ integer(item.registrations) }}</td>
                      <td>{{ percent(dashboard.audience.summary.registrations ? (item.registrations / dashboard.audience.summary.registrations * 100) : 0) }}</td>
                      <td>{{ integer(item.payingCustomers) }}</td>
                      <td>{{ percent(item.conversionPercent) }}</td>
                      <td>{{ money(item.gmv) }}</td>
                    </tr>
                    <tr v-if="!dashboard.audience.registrationSources.length"><td colspan="6">Регистраций в периоде нет.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="two-column-grid">
              <div class="panel">
                <div class="panel-heading analytics-panel-heading"><div><h2>Платформы последнего входа</h2>
                  <p>Пользователи, у которых последняя авторизация пришлась на выбранный период; НЕ число авторизаций</p></div></div>
                <div class="source-list">
                  <div v-for="item in dashboard.audience.latestLoginPlatforms" :key="item.platform" class="source-row">
                    <div class="source-head"><strong>{{ audienceLabel(item.platform) }}</strong><span>{{ integer(item.users) }}</span></div>
                    <div class="progress-track"><div class="progress-fill muted" :style="{ width: `${loginSampleUsers ? 100 * item.users / loginSampleUsers : 0}%` }"></div></div>
                  </div>
                  <div v-if="!dashboard.audience.latestLoginPlatforms.length" class="chart-empty">Нет зафиксированных последних входов в периоде.</div>
                </div>
              </div>
              <div class="panel">
                <div class="panel-heading analytics-panel-heading"><div><h2>Способы последнего входа</h2><p>Одно сохранённое значение на пользователя</p></div></div>
                <div class="ranking-list">
                  <div v-for="item in dashboard.audience.latestLoginMethods" :key="item.method" class="ranking-row">
                    <div class="ranking-main"><strong>{{ audienceLabel(item.method) }}</strong></div>
                    <div class="ranking-value">{{ integer(item.users) }}</div>
                  </div>
                  <div v-if="!dashboard.audience.latestLoginMethods.length" class="chart-empty">Нет данных.</div>
                </div>
              </div>
            </div>

            <div class="two-column-grid">
              <div class="panel">
                <div class="panel-heading analytics-panel-heading"><div><h2>Последняя активность*</h2>
                  <p>Снимок на {{ formatDateTime(dashboard.audience.lastActivity.asOf) }}; это не исторические DAU/WAU/MAU</p></div></div>
                <div class="mini-kpi-grid">
                  <div class="mini-kpi"><span>Последние 24 часа</span><strong>{{ integer(dashboard.audience.lastActivity.last24h) }}</strong></div>
                  <div class="mini-kpi"><span>Последние 7 суток</span><strong>{{ integer(dashboard.audience.lastActivity.last7d) }}</strong></div>
                  <div class="mini-kpi"><span>Последние 30 суток</span><strong>{{ integer(dashboard.audience.lastActivity.last30d) }}</strong></div>
                </div>
              </div>
              <div class="panel">
                <div class="panel-heading analytics-panel-heading"><div><h2>Устройства и Push*</h2>
                  <p>Сохранённые токены, не установки и не подтверждённая доставка</p></div></div>
                <div class="ranking-list">
                  <div v-for="item in dashboard.audience.pushDevices.platforms" :key="item.platform" class="ranking-row">
                    <div class="ranking-main"><strong>{{ audienceLabel(item.platform) }}</strong><span>{{ integer(item.users) }} пользователей</span></div>
                    <div class="ranking-value">{{ integer(item.tokens) }} токенов</div>
                  </div>
                  <div v-if="!dashboard.audience.pushDevices.platforms.length" class="chart-empty">Нет зарегистрированных токенов.</div>
                </div>
              </div>
            </div>

            <div class="two-column-grid">
              <div class="panel">
                <div class="panel-heading analytics-panel-heading"><div><h2>Версии приложений*</h2><p>ТОП-10 версий и сборок среди сохранённых Push-токенов</p></div></div>
                <div class="responsive-table">
                  <table><thead><tr><th>Версия</th><th>Пользователи</th><th>Токены</th></tr></thead>
                    <tbody>
                      <tr v-for="item in dashboard.audience.pushDevices.versionsTop10" :key="`${item.version}:${item.build}`">
                        <td>
                          {{ audienceLabel(item.version) }}
                          <small v-if="item.build !== 'unknown'"> · сборка {{ item.build }}</small>
                        </td>
                        <td>{{ integer(item.users) }}</td>
                        <td>{{ integer(item.tokens) }}</td>
                      </tr>
                      <tr v-if="!dashboard.audience.pushDevices.versionsTop10.length"><td colspan="3">Нет данных.</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="panel">
                <div class="panel-heading analytics-panel-heading"><div><h2>Каналы создания заказов</h2>
                  <p>Заказы, созданные в выбранном периоде; неизвестные каналы не восстанавливаем задним числом</p></div></div>
                <div class="responsive-table">
                  <table><thead><tr><th>Канал</th><th>Всего</th><th>Оплачено</th><th>GMV</th></tr></thead>
                    <tbody>
                      <tr v-for="item in dashboard.audience.orderChannels" :key="item.channel">
                        <td>{{ audienceLabel(item.channel) }}</td><td>{{ integer(item.orders) }}</td>
                        <td>{{ integer(item.paidOrders) }}</td><td>{{ money(item.gmv) }}</td>
                      </tr>
                      <tr v-if="!dashboard.audience.orderChannels.length"><td colspan="4">Заказов в периоде нет.</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="definition-note">
              <strong>Ограничения данных.</strong>
              Нет полной истории входов и просмотров. «Неизвестно» — честная категория для старых записей.
              Период применён к регистрации, последнему входу и созданию заказа; показатели со звёздочкой — актуальные снимки.
              Старые пользователи не получают выдуманный источник. Доставка Push не проверяется этой аналитикой.
            </div>
          </template>
        </section>

        <section v-if="activeTab === 'retention'" class="analytics-section">
          <div class="retention-summary">
            <div><span>90 дней</span><strong>{{ percent(dashboard.retention.windows.d90) }}</strong></div>
            <div><span>180 дней</span><strong>{{ percent(dashboard.retention.windows.d180) }}</strong></div>
            <div><span>365 дней</span><strong>{{ percent(dashboard.retention.windows.d365) }}</strong></div>
          </div>
          <div class="panel">
            <div class="panel-heading analytics-panel-heading">
              <div>
                <h2>Purchase cohorts</h2>
                <p>Повторная оплаченная покупка после первой; незрелые когорты помечены «—»</p>
              </div>
            </div>
            <div class="responsive-table cohort-table">
              <table>
                <thead><tr><th>Первая покупка</th><th>Клиентов</th><th>3 мес.</th><th>6 мес.</th><th>12 мес.</th></tr></thead>
                <tbody>
                  <tr v-for="row in dashboard.retention.cohorts" :key="row.cohort">
                    <td><strong>{{ formatMonth(row.cohort) }}</strong></td><td>{{ integer(row.customers) }}</td>
                    <td :style="heatStyle(row.m3)">{{ nullablePercent(row.m3) }}</td>
                    <td :style="heatStyle(row.m6)">{{ nullablePercent(row.m6) }}</td>
                    <td :style="heatStyle(row.m12)">{{ nullablePercent(row.m12) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section v-if="activeTab === 'operations'" class="analytics-section">
          <div class="kpi-grid compact">
            <KpiSimple label="Создано заказов" :value="integer(dashboard.operations.createdOrders)" />
            <KpiSimple label="Оплачено" :value="integer(dashboard.operations.paidOrders)" />
            <KpiSimple label="Выполнено" :value="integer(dashboard.operations.fulfilledOrders)" />
            <KpiSimple label="Fulfillment" :value="percent(dashboard.operations.fulfillmentRate)" />
            <KpiSimple label="Отмены" :value="percent(dashboard.operations.cancellationRate)" />
            <KpiSimple label="Споры" :value="percent(dashboard.operations.disputeRate)" />
            <KpiSimple label="Медиана выполнения" :value="`${number(dashboard.operations.medianFulfillmentDays)} дн.`" />
            <KpiSimple label="Среднее выполнение" :value="`${number(dashboard.operations.avgFulfillmentDays)} дн.`" />
            <KpiSimple label="Активные исполнители" :value="integer(dashboard.operations.activeContractors)" />
            <KpiSimple label="Reassignment" :value="percent(dashboard.operations.reassignmentRate)" />
            <KpiSimple label="Назначений / заказ" :value="number(dashboard.operations.avgAssignmentsPerOrder)" />
            <KpiSimple label="5★ доля" :value="percent(dashboard.operations.fiveStarShare)" />
          </div>
          <div class="panel">
            <div class="panel-heading analytics-panel-heading"><div><h2>География исполнения</h2><p>Оплаченные заказы текущего периода</p></div></div>
            <div class="responsive-table">
              <table>
                <thead><tr><th>Регион</th><th>Заказы</th><th>Клиенты</th><th>GMV</th><th>AOV</th><th>Fulfillment</th></tr></thead>
                <tbody>
                  <tr v-for="region in dashboard.regions" :key="region.id">
                    <td><strong>{{ region.name }}</strong></td>
                    <td>{{ integer(region.orders) }}</td>
                    <td>{{ integer(region.customers) }}</td>
                    <td>{{ money(region.gmv) }}</td>
                    <td>{{ money(region.aov) }}</td>
                    <td>{{ percent(region.fulfillmentRate) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <div class="definition-note">
          <strong>Важно о формулах.</strong>
          GMV считается по customer-payment статусам <code>CONFIRMED / PAY_CHECKING / AUTHORIZED</code>.
          <code>payment_success</code> намеренно не используется — в текущем backend это поле связано с выплатой исполнителю.
          «Сервисная комиссия» — <code>service_commission_amount</code>, а не бухгалтерская выручка.
        </div>
      </template>

      <div v-else-if="loading" class="analytics-loading">Собираю показатели…</div>
    </div>
  </div>
</template>

<script>
import { saveAs } from 'file-saver';
import { analyticsDashboard } from '@/api/settings';

const KpiSimple = {
  props: ['label', 'value'],
  template: '<div class="kpi-card simple"><div class="kpi-topline"><span>{{ label }}</span></div><div class="kpi-value">{{ value }}</div></div>',
};

export default {
  components: { KpiSimple },
  data() {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth() - 11, 1);
    return {
      dashboard: null,
      loading: false,
      error: '',
      activeTab: 'overview',
      activeQuickRange: '12m',
      chartMetric: 'gmv',
      tabs: [
        { id: 'overview', label: 'Обзор' },
        { id: 'sales', label: 'Продажи' },
        { id: 'customers', label: 'Клиенты' },
        { id: 'audience', label: 'Аудитория и каналы' },
        { id: 'retention', label: 'Retention' },
        { id: 'operations', label: 'Операции' },
      ],
      quickRanges: [
        { value: '7d', label: '7 дней' },
        { value: '30d', label: '30 дней' },
        { value: 'month', label: 'Месяц' },
        { value: '90d', label: '90 дней' },
        { value: 'ytd', label: 'YTD' },
        { value: '12m', label: '12 месяцев' },
      ],
      filters: {
        from: this.toDateInput(from),
        to: this.toDateInput(today),
        compare: 'year',
        granularity: 'month',
        regionId: '',
        categoryId: '',
        partnerId: '',
        source: 'all',
        state: 'all',
      },
      chartMetrics: [
        { key: 'gmv', label: 'GMV', seriesKey: 'gmv' },
        { key: 'serviceCommission', label: 'Комиссия', seriesKey: 'serviceCommission' },
        { key: 'paidOrders', label: 'Заказы', seriesKey: 'orders' },
        { key: 'payingCustomers', label: 'Клиенты', seriesKey: 'payingCustomers' },
      ],
    };
  },
  computed: {
    generatedAt() { return this.dashboard && this.dashboard.meta && this.dashboard.meta.generatedAt; },
    loginSampleUsers() {
      const rows = this.dashboard && this.dashboard.audience && this.dashboard.audience.latestLoginPlatforms;
      return (rows || []).reduce((sum, item) => sum + Number(item.users || 0), 0);
    },
    hasComparison() { return !!(this.dashboard && this.dashboard.meta && this.dashboard.meta.compare !== 'none'); },
    comparisonLabel() {
      if (!this.dashboard || !this.dashboard.meta || this.dashboard.meta.compare === 'none') return 'Без сравнения';
      return this.dashboard.meta.compare === 'year' ? 'Прошлый год' : 'Предыдущий период';
    },
    periodCaption() {
      if (!this.dashboard) return '';
      return `${this.formatDate(this.dashboard.meta.from)} — ${this.formatDate(this.dashboard.meta.to)} · ${this.comparisonLabel}`;
    },
    primaryKpis() {
      if (!this.dashboard) return [];
      const k = this.dashboard.kpis || {};
      return [
        {
          key: 'gmv',
          label: 'GMV',
          metric: k.gmv,
          hint: 'Оплаченные customer-payment заказы, без REFUNDED.',
        },
        {
          key: 'serviceCommission',
          label: 'Сервисная комиссия',
          metric: k.serviceCommission,
          hint: 'service_commission_amount; не бухгалтерская выручка.',
        },
        { key: 'paidOrders', label: 'Оплаченные заказы', metric: k.paidOrders },
        { key: 'payingCustomers', label: 'Платящие клиенты', metric: k.payingCustomers },
        { key: 'aov', label: 'Средний чек', metric: k.aov },
        { key: 'repeatRate', label: 'Repeat rate', metric: k.repeatRate },
        { key: 'fulfillmentRate', label: 'Fulfillment', metric: k.fulfillmentRate },
        { key: 'newPayingCustomers', label: 'Новые платящие', metric: k.newPayingCustomers },
      ];
    },
    activeChartConfig() { return this.chartMetrics.find((item) => item.key === this.chartMetric) || this.chartMetrics[0]; },
    currentChartValues() { return this.seriesValues(this.dashboard && this.dashboard.series); },
    comparisonChartValues() { return this.seriesValues(this.dashboard && this.dashboard.comparisonSeries); },
    currentChartPoints() { return this.linePoints(this.currentChartValues); },
    comparisonChartPoints() { return this.hasComparison ? this.linePoints(this.comparisonChartValues) : ''; },
    hasSeriesData() { return this.currentChartValues.some((value) => Number(value) !== 0); },
    chartAxisLabels() {
      const rows = (this.dashboard && this.dashboard.series) || [];
      if (!rows.length) return [];
      const maxLabels = 6;
      const step = Math.max(1, Math.ceil(rows.length / maxLabels));
      return rows
        .filter((row, index) => index % step === 0 || index === rows.length - 1)
        .map((row, index) => ({
          key: `${row.period}-${index}`,
          label: this.formatPeriod(row.period),
        }));
    },
  },
  mounted() { this.fetchDashboard(); },
  methods: {
    toDateInput(date) {
      const value = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
      return value.toISOString().slice(0, 10);
    },
    addDays(date, days) { const d = new Date(date); d.setDate(d.getDate() + days); return d; },
    setQuickRange(value) {
      const now = new Date();
      let from = new Date(now);
      if (value === '7d') from = this.addDays(now, -6);
      if (value === '30d') from = this.addDays(now, -29);
      if (value === '90d') from = this.addDays(now, -89);
      if (value === 'month') from = new Date(now.getFullYear(), now.getMonth(), 1);
      if (value === 'ytd') from = new Date(now.getFullYear(), 0, 1);
      if (value === '12m') from = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      this.filters.from = this.toDateInput(from);
      this.filters.to = this.toDateInput(now);
      if (['7d', '30d'].includes(value)) {
        this.filters.granularity = 'day';
      } else if (value === '90d') {
        this.filters.granularity = 'week';
      } else {
        this.filters.granularity = 'month';
      }
      this.activeQuickRange = value;
      this.fetchDashboard();
    },
    resetDimensionFilters() {
      this.filters.regionId = '';
      this.filters.categoryId = '';
      this.filters.partnerId = '';
      this.filters.source = 'all';
      this.filters.state = 'all';
      this.fetchDashboard();
    },
    applyFilters() { this.activeQuickRange = ''; this.fetchDashboard(); },
    switchTab(id) {
      this.activeTab = id;
      if (id === 'audience' && (!this.dashboard || !this.dashboard.audience)) this.fetchDashboard();
    },
    selectChartMetric(key) {
      if (this.chartMetrics.some((item) => item.key === key)) this.chartMetric = key;
    },
    async fetchDashboard() {
      this.loading = true;
      this.error = '';
      try {
        const response = await analyticsDashboard({
          from: this.filters.from,
          to: this.filters.to,
          compare: this.filters.compare,
          granularity: this.filters.granularity,
          include_audience: this.activeTab === 'audience' ? 1 : 0,
          region_id: this.filters.regionId || undefined,
          category_id: this.filters.categoryId || undefined,
          partner_id: this.filters.partnerId || undefined,
          source: this.filters.source,
          state: this.filters.state,
        });
        this.dashboard = response.data;
      } catch (error) {
        this.error = error && error.response && error.response.status === 403
          ? 'Недостаточно прав для просмотра.'
          : 'Проверьте доступность backend и корректность выбранного периода.';
      } finally {
        this.loading = false;
      }
    },
    optionList(key) { return (this.dashboard && this.dashboard.filters && this.dashboard.filters[key]) || []; },
    kpiValue(key) { return this.dashboard && this.dashboard.kpis && this.dashboard.kpis[key] ? this.dashboard.kpis[key].value : 0; },
    formatMetric(metric) {
      if (!metric) return '—';
      if (metric.unit === 'money') return this.money(metric.value);
      if (metric.unit === 'percent') return this.percent(metric.value);
      return this.integer(metric.value);
    },
    money(value) { return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Number(value || 0))} ₽`; },
    integer(value) { return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Number(value || 0)); },
    number(value) { return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(Number(value || 0)); },
    percent(value) { return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(Number(value || 0))}%`; },
    nullablePercent(value) { return value === null || typeof value === 'undefined' ? '—' : this.percent(value); },
    formatDelta(value) {
      if (value === null || typeof value === 'undefined') return '—';
      const n = Number(value);
      const sign = n > 0 ? '+' : '';
      return `${sign}${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(n)}%`;
    },
    deltaClass(value) {
      const n = Number(value);
      let modifier = 'neutral';
      if (n > 0) modifier = 'positive';
      if (n < 0) modifier = 'negative';
      return ['delta', modifier];
    },
    formatDate(value) { if (!value) return ''; return new Intl.DateTimeFormat('ru-RU').format(new Date(`${value}T00:00:00`)); },
    formatDateTime(value) {
      if (!value) return '';
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value));
    },
    formatMonth(value) {
      if (!value) return '';
      const [year, month] = value.split('-');
      return new Intl.DateTimeFormat('ru-RU', {
        month: 'short',
        year: 'numeric',
      }).format(new Date(Number(year), Number(month) - 1, 1));
    },
    formatPeriod(value) { if (!value) return ''; if (/^\d{4}-\d{2}$/.test(value)) return this.formatMonth(value); return this.formatDate(value); },
    seriesValues(rows) { const key = this.activeChartConfig.seriesKey; return (rows || []).map((row) => Number(row[key] || 0)); },
    linePoints(values) {
      if (!values || !values.length) return '';
      const all = [...this.currentChartValues, ...(this.hasComparison ? this.comparisonChartValues : [])];
      const max = Math.max(1, ...all.map((value) => Number(value || 0)));
      const min = Math.min(0, ...all.map((value) => Number(value || 0)));
      const range = Math.max(1, max - min);
      const left = 52;
      const right = 892;
      const top = 28;
      const bottom = 272;
      return values.map((value, index) => {
        const x = values.length === 1 ? (left + right) / 2 : left + ((right - left) * index) / (values.length - 1);
        const y = bottom - ((Number(value || 0) - min) / range) * (bottom - top);
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      }).join(' ');
    },
    heatStyle(value) {
      if (value === null || typeof value === 'undefined') return {};
      const alpha = Math.min(0.24, Math.max(0.04, Number(value) / 250));
      return { backgroundColor: `rgba(42, 117, 87, ${alpha})`, fontWeight: 700 };
    },
    audienceLabel(key) {
      const labels = {
        unknown: 'Неизвестно',
        android: 'Android',
        ios: 'iOS',
        web: 'Web',
        android_app: 'Приложение Android',
        ios_app: 'Приложение iOS',
        iwaly_web: 'Сайт iWALY',
        direct_registration: 'Прямая регистрация (платформа не указана)',
        partner_form: 'Партнёрская форма',
        partner_external_registration: 'Внешняя регистрация партнёра',
        external_registration: 'Внешняя регистрация',
        max_miniapp: 'MAX Mini App',
        phone_password: 'Телефон и пароль',
        phone_password_registration: 'Вход при регистрации',
        mobile_id: 'Mobile ID',
        web_handoff: 'Web handoff',
        external_handoff: 'Внешняя авторизация',
        partner_order_form: 'Партнёрский заказ',
        sber: 'Сбер ID',
        max: 'MAX',
      };
      return labels[key] || key || 'Неизвестно';
    },
    downloadCsv() {
      if (!this.dashboard) return;
      const rows = [['KPI', 'Значение', 'Единица', 'Предыдущий период', 'Изменение %']];
      Object.keys(this.dashboard.kpis || {}).forEach((key) => {
        const metric = this.dashboard.kpis[key];
        rows.push([key, metric.value, metric.unit, metric.previousValue ?? '', metric.deltaPercent ?? '']);
      });
      rows.push([]);
      rows.push(['Период', 'GMV', 'Сервисная комиссия', 'Заказы', 'Платящие клиенты', 'Новые', 'Повторные']);
      (this.dashboard.series || []).forEach((row) => rows.push([
        row.period,
        row.gmv,
        row.serviceCommission,
        row.orders,
        row.payingCustomers,
        row.newCustomers,
        row.repeatCustomers,
      ]));
      const { audience } = this.dashboard;
      if (audience && audience.available) {
        rows.push([]);
        rows.push(['Аудитория: регистрации', 'Источник', 'Регистрации', 'Платящие', 'Конверсия %', 'GMV']);
        (audience.registrationSources || []).forEach((item) => rows.push([
          'Источник', item.source, item.registrations, item.payingCustomers, item.conversionPercent, item.gmv,
        ]));
        rows.push([]);
        rows.push(['Последний вход: платформа', 'Пользователей']);
        (audience.latestLoginPlatforms || []).forEach((item) => rows.push([item.platform, item.users]));
        rows.push(['Последний вход: способ', 'Пользователей']);
        (audience.latestLoginMethods || []).forEach((item) => rows.push([item.method, item.users]));
        rows.push(['Активность (скользящий снимок)', '24ч', '7д', '30д']);
        rows.push(['Последняя активность', audience.lastActivity.last24h, audience.lastActivity.last7d, audience.lastActivity.last30d]);
        rows.push(['Push токены (снимок)', audience.pushDevices.registeredTokens, 'Пользователи', audience.pushDevices.registeredUsers]);
        rows.push(['Платформа Push', 'Токены', 'Пользователи']);
        (audience.pushDevices.platforms || []).forEach((item) => rows.push([item.platform, item.tokens, item.users]));
        rows.push(['Версия приложения', 'Токены', 'Пользователи']);
        (audience.pushDevices.versionsTop10 || []).forEach((item) => rows.push([`${item.version}+${item.build}`, item.tokens, item.users]));
        rows.push(['Канал заказа', 'Заказы', 'Оплачено', 'GMV']);
        (audience.orderChannels || []).forEach((item) => rows.push([item.channel, item.orders, item.paidOrders, item.gmv]));
      }
      const csvRows = rows.map((row) => row
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(';'));
      const csv = `\ufeff${csvRows.join('\n')}`;
      saveAs(
        new Blob([csv], { type: 'text/csv;charset=utf-8' }),
        `iwaly-analytics-${this.filters.from}-${this.filters.to}.csv`,
      );
    },
  },
};
</script>

<style lang="scss">
.analytics-page  {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  background: #f4f6f7;
}
.analytics-page *,
.analytics-page *::before,
.analytics-page *::after  {
  box-sizing: border-box;
}
.analytics-wrap  {
  padding: 24px;
  max-width: 1680px;
  width: 100%;
  min-width: 0;
  margin: 0 auto;
  box-sizing: border-box;
}
.analytics-header  {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 18px;
}
.analytics-title-row  {
  display: flex;
  align-items: center;
  gap: 12px;
}
.analytics-title-row h1  {
  margin: 0;
  font-size: 27px;
  line-height: 1.2;
  color: #17211d;
}
.analytics-header p  {
  margin: 7px 0 0;
  color: #68746f;
  font-size: 14px;
}
.analytics-badge  {
  background: #e7f0ec;
  color: #286c54;
  border: 1px solid #c9ded5;
  border-radius: 999px;
  padding: 4px 9px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .04em;
}
.analytics-actions  {
  display: flex;
  align-items: center;
  gap: 9px;
}
.updated-at  {
  color: #7b8581;
  font-size: 12px;
  white-space: nowrap;
}
.analytics-btn  {
  border: 1px solid transparent;
  border-radius: 7px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.analytics-btn.primary  {
  background: #276d55;
  color: #fff;
}
.analytics-btn.secondary  {
  background: #fff;
  border-color: #d7ddda;
  color: #33443e;
}
.analytics-btn:disabled  {
  opacity: .55;
  cursor: default;
}
.quick-range-row  {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 10px;
}
.quick-range  {
  background: #fff;
  border: 1px solid #dbe0de;
  border-radius: 999px;
  padding: 7px 12px;
  color: #58645f;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}
.quick-range.active  {
  background: #2c7159;
  border-color: #2c7159;
  color: #fff;
}
.filter-panel  {
  display: grid;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
  gap: 10px;
  background: #fff;
  border: 1px solid #e0e5e3;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 18px;
  box-shadow: 0 1px 2px rgba(24, 42, 35, .03);
}
.filter-panel label  {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.filter-panel label > span  {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #7a8581;
  font-weight: 800;
}
.filter-panel input, .filter-panel select  {
  height: 38px;
  margin: 0;
  border: 1px solid #d7ddda;
  border-radius: 7px;
  background: #fff;
  padding: 0 10px;
  font-size: 13px;
  box-shadow: none;
}
.filter-apply  {
  display: flex;
  align-items: end;
  gap: 7px;
}
.subtabs  {
  display: flex;
  gap: 3px;
  border-bottom: 1px solid #dce2df;
  margin-bottom: 18px;
  overflow-x: auto;
}
.subtab  {
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  padding: 11px 15px 10px;
  color: #6b7772;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
}
.subtab.active  {
  color: #245f4b;
  border-bottom-color: #2d775d;
}
.analytics-error  {
  background: #fff2f0;
  border: 1px solid #efcbc5;
  color: #934439;
  padding: 12px 14px;
  border-radius: 9px;
  margin-bottom: 16px;
}
.analytics-loading  {
  padding: 60px;
  text-align: center;
  color: #78837f;
  font-weight: 700;
}
.analytics-section  {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.kpi-grid  {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 12px;
}
.kpi-grid.compact  {
  grid-template-columns: repeat(4, minmax(170px, 1fr));
}
.kpi-card  {
  appearance: none;
  text-align: left;
  background: #fff;
  border: 1px solid #e0e5e3;
  border-radius: 11px;
  padding: 15px 16px;
  box-shadow: 0 1px 2px rgba(27, 45, 38, .035);
  color: inherit;
}
button.kpi-card  {
  cursor: pointer;
  transition: transform .12s ease, box-shadow .12s ease;
}
button.kpi-card:hover  {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(27, 45, 38, .07);
}
.kpi-card.simple  {
  min-height: 108px;
}
.kpi-topline  {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #66736e;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .025em;
}
.kpi-hint  {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #edf2f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: none;
  color: #6d7b75;
}
.kpi-value  {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.25;
  color: #18251f;
  margin-top: 8px;
}
.kpi-comparison  {
  display: flex;
  gap: 5px;
  align-items: center;
  min-height: 20px;
  margin-top: 5px;
  color: #8a9490;
  font-size: 11px;
}
.delta  {
  font-weight: 800;
}
.delta.positive  {
  color: #258257;
}
.delta.negative  {
  color: #bd554c;
}
.delta.neutral  {
  color: #7d8884;
}
.panel  {
  background: #fff;
  border: 1px solid #e0e5e3;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(27,45,38,.035);
  overflow: hidden;
}
.analytics-panel-heading  {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid #edf0ef;
}
.analytics-panel-heading h2  {
  margin: 0;
  font-size: 16px;
  color: #24312c;
}
.analytics-panel-heading p  {
  margin: 4px 0 0;
  color: #818b87;
  font-size: 12px;
}
.metric-switcher  {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.metric-switcher button  {
  border: 1px solid #dbe1de;
  background: #fff;
  color: #66736e;
  border-radius: 6px;
  padding: 6px 9px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.metric-switcher button.active  {
  background: #e8f1ed;
  border-color: #bad4c9;
  color: #245f4b;
}
.trend-chart-wrap  {
  position: relative;
  padding: 12px 12px 0;
}
.trend-chart  {
  display: block;
  width: 100%;
  height: 300px;
}
.grid-line  {
  stroke: #edf1ef;
  stroke-width: 1;
}
.trend-line  {
  fill: none;
  vector-effect: non-scaling-stroke;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.trend-line.current  {
  stroke: #2e785d;
}
.trend-line.comparison  {
  stroke: #99a6a1;
  stroke-width: 1.8;
  stroke-dasharray: 6 5;
}
.chart-legend  {
  position: absolute;
  top: 15px;
  left: 24px;
  display: flex;
  gap: 14px;
  font-size: 11px;
  color: #71807a;
}
.chart-legend span  {
  display: flex;
  align-items: center;
  gap: 5px;
}
.legend-line  {
  width: 18px;
  height: 2px;
  display: inline-block;
}
.legend-line.current  {
  background: #2e785d;
}
.legend-line.comparison  {
  border-top: 2px dashed #99a6a1;
}
.chart-empty  {
  position: absolute;
  inset: 50% 0 auto;
  text-align: center;
  color: #9aa39f;
  font-size: 13px;
}
.axis-labels  {
  display: flex;
  justify-content: space-between;
  padding: 0 18px 12px 52px;
  color: #89938f;
  font-size: 10px;
}
.two-column-grid  {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.funnel-list, .source-list  {
  padding: 14px 18px 18px;
}
.funnel-row, .source-row  {
  margin-bottom: 15px;
}
.funnel-row:last-child, .source-row:last-child  {
  margin-bottom: 0;
}
.funnel-label, .source-head  {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #34433d;
}
.progress-track  {
  height: 8px;
  border-radius: 999px;
  background: #edf1ef;
  overflow: hidden;
  margin: 6px 0 4px;
}
.progress-fill  {
  height: 100%;
  background: #3c866b;
  border-radius: inherit;
}
.progress-fill.muted  {
  background: #729e8e;
}
.funnel-meta, .source-meta  {
  color: #8a9490;
  font-size: 10px;
}
.ranking-list  {
  padding: 6px 18px 14px;
}
.ranking-row  {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 11px 0;
  border-bottom: 1px solid #f0f2f1;
}
.ranking-row:last-child  {
  border-bottom: 0;
}
.rank  {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #eef3f1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #60716a;
  font-size: 11px;
  font-weight: 800;
}
.ranking-main  {
  display: flex;
  flex-direction: column;
}
.ranking-main strong  {
  color: #324139;
  font-size: 13px;
}
.ranking-main span  {
  color: #8b9591;
  font-size: 10px;
  margin-top: 2px;
}
.ranking-value  {
  font-weight: 800;
  color: #27372f;
  font-size: 13px;
}
.mini-kpi-grid  {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 12px 18px 18px;
  gap: 9px;
}
.mini-kpi  {
  background: #f8faf9;
  border: 1px solid #edf0ef;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mini-kpi span  {
  font-size: 11px;
  color: #7c8883;
}
.mini-kpi strong  {
  font-size: 19px;
  color: #26372f;
}
.responsive-table  {
  overflow-x: auto;
}
.responsive-table table  {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
}
.responsive-table th  {
  text-align: left;
  background: #f8faf9;
  color: #73807a;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: .035em;
  padding: 10px 13px;
  border-bottom: 1px solid #e9edeb;
  white-space: nowrap;
}
.responsive-table td  {
  padding: 11px 13px;
  border-bottom: 1px solid #eef1f0;
  color: #43514b;
  font-size: 12px;
}
.responsive-table tbody tr:last-child td  {
  border-bottom: 0;
}
.funnel-horizontal  {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1px;
  padding: 18px;
}
.funnel-stage  {
  background: #f4f8f6;
  padding: 18px 13px;
  position: relative;
  min-height: 106px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.funnel-stage:first-child  {
  border-radius: 9px 0 0 9px;
}
.funnel-stage:last-child  {
  border-radius: 0 9px 9px 0;
}
.funnel-stage span  {
  font-size: 11px;
  color: #73817b;
  font-weight: 700;
}
.funnel-stage strong  {
  font-size: 24px;
  color: #26372f;
}
.funnel-stage small  {
  color: #2f765c;
  font-weight: 800;
}
.retention-summary  {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.retention-summary > div  {
  background: #fff;
  border: 1px solid #e0e5e3;
  border-radius: 11px;
  padding: 17px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.retention-summary span  {
  color: #6e7a75;
  font-size: 12px;
  font-weight: 800;
}
.retention-summary strong  {
  font-size: 24px;
  color: #245f4b;
}
.cohort-table td:not(:first-child)  {
  text-align: center;
}
.cohort-table th:not(:first-child)  {
  text-align: center;
}
.definition-note  {
  background: #f8faf9;
  border: 1px solid #dfe6e2;
  border-radius: 10px;
  padding: 13px 15px;
  color: #68756f;
  font-size: 11px;
  line-height: 1.55;
}
.definition-note strong  {
  color: #3d4c45;
}
.definition-note code  {
  background: #edf2f0;
  border-radius: 4px;
  padding: 1px 4px;
  color: #3c5e50;
}
@media (max-width: 1250px)  {
  .filter-panel  {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }
  .kpi-grid, .kpi-grid.compact  {
    grid-template-columns: repeat(2, minmax(170px, 1fr));
  }
}
@media (max-width: 850px)  {
  .analytics-wrap  {
    padding: 14px;
  }
  .analytics-header  {
    flex-direction: column;
  }
  .analytics-actions  {
    width: 100%;
    flex-wrap: wrap;
  }
  .filter-panel  {
    grid-template-columns: 1fr 1fr;
  }
  .two-column-grid  {
    grid-template-columns: 1fr;
  }
  .funnel-horizontal  {
    grid-template-columns: 1fr;
  }
  .funnel-stage:first-child, .funnel-stage:last-child  {
    border-radius: 8px;
  }
  .retention-summary  {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px)  {
  .analytics-page  {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: hidden;
  }
  .analytics-wrap  {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 10px 8px 24px;
    overflow-x: hidden;
    box-sizing: border-box;
  }
  .analytics-header > div,
  .analytics-title-row,
  .analytics-actions,
  .quick-range-row,
  .filter-panel,
  .analytics-section,
  .panel,
  .two-column-grid,
  .trend-chart-wrap  {
    min-width: 0;
  }
  .analytics-header  {
    gap: 10px;
    margin-bottom: 12px;
  }
  .analytics-title-row  {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 7px;
  }
  .analytics-title-row h1  {
    font-size: 21px;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }
  .analytics-header p  {
    max-width: 100%;
    margin-top: 5px;
    font-size: 12px;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }
  .analytics-actions  {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, auto));
    justify-content: start;
    width: 100%;
    gap: 6px;
  }
  .analytics-actions .updated-at  {
    grid-column: 1 / -1;
    white-space: normal;
  }
  .analytics-btn,
  .quick-range,
  .metric-switcher button  {
    min-height: 36px;
  }
  .quick-range-row  {
    width: 100%;
    max-width: 100%;
    flex-wrap: wrap;
    gap: 5px;
    margin-bottom: 8px;
    overflow: visible;
  }
  .quick-range  {
    flex: 0 1 auto;
    max-width: 100%;
    padding: 6px 10px;
    white-space: normal;
  }
  .filter-panel  {
    width: 100%;
    max-width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    padding: 10px;
    margin-bottom: 12px;
    box-sizing: border-box;
  }
  .filter-panel label,
  .filter-panel input,
  .filter-panel select  {
    min-width: 0;
    width: 100%;
  }
  .filter-panel input,
  .filter-panel select  {
    height: 40px;
    font-size: 16px;
  }
  .filter-apply  {
    align-items: stretch;
    grid-column: 1 / -1;
  }
  .filter-apply .analytics-btn  {
    flex: 1 1 0;
  }
  .subtabs  {
    margin: 0 -8px 12px;
    padding: 0 8px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .subtabs::-webkit-scrollbar  {
    display: none;
  }
  .subtab  {
    min-height: 40px;
    padding: 9px 12px 8px;
  }
  .kpi-grid,
  .kpi-grid.compact  {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 7px;
  }
  .kpi-card  {
    min-width: 0;
    padding: 10px;
  }
  .kpi-card.simple  {
    min-height: 88px;
  }
  .kpi-topline  {
    gap: 5px;
    font-size: 10px;
    line-height: 1.25;
  }
  .kpi-value  {
    margin-top: 6px;
    font-size: 20px;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }
  .kpi-comparison  {
    flex-wrap: wrap;
    margin-top: 3px;
  }
  .analytics-panel-heading  {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    padding: 11px 12px;
  }
  .metric-switcher  {
    width: 100%;
  }
  .metric-switcher button  {
    flex: 1 1 auto;
    padding: 6px 8px;
  }
  .trend-chart-wrap  {
    padding: 8px 6px 0;
  }
  .trend-chart  {
    height: 220px;
  }
  .chart-legend  {
    position: static;
    flex-wrap: wrap;
    gap: 5px 12px;
    padding: 0 8px 6px;
  }
  .axis-labels  {
    padding: 0 8px 10px;
  }
  .ranking-list,
  .funnel-list,
  .source-list  {
    padding-left: 12px;
    padding-right: 12px;
  }
  .ranking-row  {
    grid-template-columns: 24px minmax(0, 1fr);
    gap: 8px;
  }
  .ranking-value  {
    grid-column: 2;
    overflow-wrap: anywhere;
  }
  .mini-kpi-grid  {
    padding: 10px;
    gap: 7px;
  }
  .mini-kpi  {
    min-width: 0;
    padding: 9px;
  }
  .mini-kpi strong  {
    font-size: 16px;
    overflow-wrap: anywhere;
  }
  .responsive-table  {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
  .responsive-table table  {
    min-width: 560px;
  }
  .retention-summary  {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 7px;
  }
  .retention-summary > div  {
    min-width: 0;
    padding: 11px 9px;
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
}
@media (max-width: 420px)  {
  .filter-panel  {
    grid-template-columns: 1fr;
  }
  .filter-apply  {
    grid-column: auto;
  }
  .analytics-title-row h1  {
    font-size: 19px;
  }
  .analytics-badge  {
    padding: 3px 7px;
  }
}
</style>
